import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Trash2, UserPlus, Copy, Share2, Archive, RotateCcw, Send, Plus, Inbox, MoreHorizontal, ChevronRight, MessageSquare, MessageCircle, Loader2, Pencil, Settings } from "lucide-react";
import AppLayout from "../../components/AppLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { toast } from "sonner";
import api, { formatApiErrorDetail, formatCurrency, formatDate } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { useTerms } from "../../lib/BrandContext";
import { useSmartBack, withReturnTo } from "../../lib/backNavigation";
import { showPlanLimitToast } from "../../lib/upgradeCta";
import { invalidateNearLimitCache } from "../../components/NearLimitTip";
import { notifyInstallMilestone } from "../../lib/installMilestone";
import StatusBadge from "../../components/StatusBadge";
import DueChip from "../../components/DueChip";

const normalizeAgeGroup = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim().toUpperCase();
  const m = s.match(/^(\d{1,2})U$/);
  if (m) return `U${m[1]}`;
  if (/^\d{1,2}$/.test(s)) return `U${s}`;
  return s;
};

// Big tappable action button used in the "Quick actions" row.
function MiniAction({ to, onClick, icon: Icon, label, accent = "brand", testid, disabled }) {
  const ring =
    accent === "amber" ? "border-amber-200 bg-amber-50 hover:bg-amber-100" :
    accent === "ink"   ? "border-ink-200 bg-white hover:bg-ink-100"        :
    "border-brand-100 bg-brand-50 hover:bg-brand-100";
  const iconBg =
    accent === "amber" ? "bg-amber-500 text-white" :
    accent === "ink"   ? "bg-ink-900 text-white"   :
    "bg-brand text-white";
  const inner = (
    <div className={`flex items-center gap-3 rounded-2xl border p-4 transition-colors ${ring} ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
      <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconBg}`}>
        <Icon className="h-5 w-5" />
      </span>
      <span className="font-heading text-base font-bold text-ink-900">{label}</span>
    </div>
  );
  if (to) return <Link to={to} data-testid={testid} className="block focus:outline-none">{inner}</Link>;
  return (
    <button type="button" onClick={onClick} data-testid={testid} className="block w-full text-left focus:outline-none" disabled={disabled}>
      {inner}
    </button>
  );
}

// Three-state segmented control for the roster: [ All | Active | Archived ].
// Counts derive from the full `players` array (unfiltered) so a coach
// always sees an accurate All / Active / Archived split without a
// second network round-trip.
function RosterFilterHeader({ filter, setFilter, players, terms }) {
  const total = players.length;
  const active = players.filter((p) => !p.archived).length;
  const archived = players.filter((p) =>  p.archived).length;
  const options = [
    { key: "all",      label: `All (${total})`,      testid: "roster-filter-all" },
    { key: "active",   label: `Active (${active})`,   testid: "roster-filter-active" },
    { key: "archived", label: `Archived (${archived})`, testid: "roster-filter-archived" },
  ];
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <h2 className="font-heading text-lg font-bold text-ink-900" data-testid="roster-heading">
        {filter === "archived"
          ? `Archived ${terms.athletes}`
          : filter === "all"
          ? `${terms.Athletes}`
          : `${terms.Athletes}`}
      </h2>
      <div
        className="inline-flex w-full items-center gap-1 rounded-xl border border-ink-200 bg-ink-100/60 p-1 sm:w-auto"
        role="tablist"
        aria-label={`${terms.Athletes} filter`}
        data-testid="roster-filter"
      >
        {options.map((o) => {
          const isActive = filter === o.key;
          return (
            <button
              key={o.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setFilter(o.key)}
              data-testid={o.testid}
              className={`flex-1 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors sm:flex-none ${
                isActive
                  ? "bg-white text-ink-900 shadow-sm"
                  : "text-ink-600 hover:text-ink-900"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}


export default function TeamDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { terms } = useTerms();
  // Contextual Back — usually /coach/teams, but honors ?from=… when
  // the user landed here from Dashboard "recent activity" or elsewhere.
  const back = useSmartBack("/coach/teams", terms.Teams);
  // Current URL string is the `from` we pass to nested detail links
  // (fees, players) so those pages' Back buttons return here.
  const fromHere = `/coach/teams/${id}`;
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [teamCharges, setTeamCharges] = useState([]);
  const [paymentsByPlayer, setPaymentsByPlayer] = useState({});
  const [pendingInviteByPlayer, setPendingInviteByPlayer] = useState({});
  // `null` while the first fetch is in flight → the render below shows
  // a neutral skeleton instead of the confident-looking `$0.00 / 0 / 0`
  // that used to flash before the real data arrived. Real numbers land
  // via `setSnapshot({...})` inside `load()` — see the fetch block below.
  const [snapshot, setSnapshot] = useState(null);
  // Roster display filter — three states: "all" | "active" | "archived".
  // "active" is the default so coaches land on the current, working roster
  // (matches the pre-change UX). "all" is the new addition — lets a coach
  // see the FULL roster in one place (active + archived) with badges to
  // distinguish. Financial calculations are UNAFFECTED: the Team Snapshot
  // is driven entirely by /payments (see load()), which returns all
  // payment_records for the team regardless of this filter.
  const [rosterFilter, setRosterFilter] = useState("active");
  const [playerForm, setPlayerForm] = useState({ first_name: "", last_name: "", jersey: "", parent_email: "" });
  const [playerOpen, setPlayerOpen] = useState(false);
  // After Add Player completes we always create an invite and swap the
  // dialog into a Copy/Share/Send-email options panel. Cleared on close.
  const [postAddInvite, setPostAddInvite] = useState(null); // {link, email, code, playerName}
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitePlayerId, setInvitePlayerId] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [inviteFromExisting, setInviteFromExisting] = useState(false);
  // First-screen mode for the Invite Parent dialog. "choose" shows the
  // three method cards (Share / Copy / Email); "email" reveals the email
  // input for coaches who explicitly want Squad Fee to send the invite
  // for them. The share panel (post-creation view) is gated separately by
  // `inviteLink` — see the render tree below.
  const [inviteMode, setInviteMode] = useState("choose");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  // --- Team-management state (Edit / Archive / Reactivate / Delete) --------
  // The team-manage dialogs are all driven from the header MoreHorizontal
  // menu. Kept in this parent so refreshing the page after a save/action
  // uses the same `load()` path as every other mutation on this screen.
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", sport: "", age_group: "", season: "" });
  const [editSubmitting, setEditSubmitting] = useState(false);
  // Delete confirm gate. `deleteConfirmText` requires the coach to
  // literally type the team name — matches destructive-action pattern
  // used elsewhere and stops "muscle memory" taps on Delete.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteChecking, setDeleteChecking] = useState(false);
  const [deletePreflight, setDeletePreflight] = useState(null); // { safe: bool, dependencies: {...} }
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [teamActionSubmitting, setTeamActionSubmitting] = useState(false);

  const load = async () => {
    try {
      const t = await api.get(`/teams/${id}`);
      setTeam(t.data);
      // Cache the current team so global nav items (e.g. the Settings tab
      // in AppLayout) can attach a contextual `?from=…&from_label=…`
      // when the coach jumps to Settings while inside this team. Cleared
      // whenever the coach navigates away to a non-team page. See
      // AppLayout's Settings link for the read side.
      try {
        sessionStorage.setItem(
          "sf_current_team",
          JSON.stringify({ id: t.data.id, name: t.data.name, path: `/coach/teams/${t.data.id}` }),
        );
      } catch { /* sessionStorage disabled — SmartBack simply falls back */ }
      // Always fetch the FULL roster (active + archived) — the segmented
      // control below filters client-side. Fetching everything in one trip
      // is what powers accurate counts on the [All | Active | Archived]
      // pills without a second network round-trip when the coach flips
      // filters. Archived players stay flagged via `p.archived`.
      const p = await api.get(`/players?team_id=${id}`);
      setPlayers(p.data);
      // Fetch this team's payment records so we can render the per-player
      // smart action (Invite Parent / Remind / Review / View) and the
      // top-of-page Team Snapshot in a single trip.
      const pr = await api.get(`/payments`, { params: { team_id: id } });
      const byPlayer = {};
      let owed = 0;
      const submittedSet = new Set();
      const unpaidPlayerSet = new Set();
      for (const r of pr.data || []) {
        const pid = r.player?.id || r.player_id;
        if (!pid) continue;
        (byPlayer[pid] = byPlayer[pid] || []).push(r);
        if (r.status === "unpaid" || r.status === "rejected") {
          owed += r.amount || 0;
          unpaidPlayerSet.add(pid);
        }
        if (r.status === "submitted") submittedSet.add(r.id);
      }
      setPaymentsByPlayer(byPlayer);
      setSnapshot({ owed, unpaid_players: unpaidPlayerSet.size, submitted_count: submittedSet.size });
      // Team-scoped fees so the coach can tap into any fee's detail page
      // directly from the team they belong to. GET /charges?team_id=X is
      // already implemented and includes confirmed/submitted counts.
      try {
        const fc = await api.get(`/charges`, { params: { team_id: id } });
        setTeamCharges(fc.data || []);
      } catch {
        setTeamCharges([]);
      }
      // Pending invites — most recent per player so the More menu can
      // resurface the link after the create dialog has been closed.
      try {
        const inv = await api.get(`/invites`, { params: { team_id: id } });
        const map = {};
        for (const it of (inv.data || [])) {
          if (it.player_id && !map[it.player_id]) map[it.player_id] = it;
        }
        setPendingInviteByPlayer(map);
      } catch {
        setPendingInviteByPlayer({});
      }
    } catch {
      setTeam(null);
      setPlayers([]);
      setTeamCharges([]);
      setPaymentsByPlayer({});
      // Keep snapshot as `null` on error — the skeleton stays visible
      // rather than lying "$0.00" while the fetch failed. If a caller
      // re-runs `load()` it will populate on success.
      setSnapshot(null);
      setPendingInviteByPlayer({});
    }
  };

  useEffect(() => { load(); /* deps intentionally narrow */ }, [id]);

  const addPlayer = async (e) => {
    e.preventDefault();
    setAddSubmitting(true);
    try {
      const { data: created } = await api.post("/players", {
        first_name: playerForm.first_name,
        last_name: playerForm.last_name,
        jersey: playerForm.jersey,
        team_id: id,
      });
      // Only create a parent invite when the coach actually typed an email.
      // Historically we auto-created a link-only invite for every new player
      // so a share link would always be reachable from the row's More menu —
      // but that silently satisfied the "Invite a parent" onboarding step
      // even when the coach never intended to invite anyone. Coaches who
      // want a shareable link without an email can still create one on
      // demand from the row's "Invite parent" action.
      let invite = null;
      const emailTrim = (playerForm.parent_email || "").trim();
      if (emailTrim) {
        try {
          const r = await api.post("/invites", { team_id: id, player_id: created.id, email: emailTrim });
          invite = r.data;
        } catch {
          // Non-fatal — player is still added; the row's Invite parent
          // action remains available if invite creation hit a transient error.
          invite = null;
        }
      }
      invalidateNearLimitCache();
      load();
      notifyInstallMilestone();
      if (invite) {
        // Swap dialog content to the post-add options panel.
        setPostAddInvite({
          link: invite.link,
          email: invite.email || "",
          code: invite.code,
          playerName: `${created.first_name} ${created.last_name}`.trim(),
        });
      } else {
        // Close immediately — coach didn't ask to invite anyone. Nudge them
        // toward the row's Invite parent action if they want to share later.
        toast.success(`${terms.Athlete} added successfully`, {
          description: "Next: Invite a parent.",
        });
        setPlayerForm({ first_name: "", last_name: "", jersey: "", parent_email: "" });
        setPlayerOpen(false);
      }
    } catch (err) {
      setPlayerOpen(false);
      invalidateNearLimitCache();
      showPlanLimitToast(err, navigate, { fallback: `Could not add ${terms.athlete}` });
    } finally {
      setAddSubmitting(false);
    }
  };

  // Closes the Add Player dialog (whether on the form step or the post-add
  // options panel) and resets all related state.
  const closeAddPlayer = () => {
    setPlayerOpen(false);
    setPostAddInvite(null);
    setPlayerForm({ first_name: "", last_name: "", jersey: "", parent_email: "" });
  };

  // Re-trigger the invite email send for the just-created invite. The
  // backend response carries `email_sent` (true / false) so we can tell
  // the coach the truth even when Resend rejected the recipient.
  const sendEmailInviteForPostAdd = async () => {
    if (!postAddInvite?.code || !postAddInvite.email) return;
    try {
      const { data } = await api.post(`/invites/${postAddInvite.code}/resend-email`);
      if (data?.email_sent) {
        toast.success(`Invitation sent to ${postAddInvite.email}`, {
          description: "Next: Create your first fee.",
        });
      } else {
        toast.warning(`We couldn't email ${postAddInvite.email}`, {
          description: "The invite link is ready — copy or share it below instead.",
        });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't send email invite";
      toast.error(typeof msg === "string" ? msg : "Couldn't send email invite");
    }
  };

  // Resend the invite email from the player More menu. Same backend call
  // as above; branches identically on `email_sent`.
  const resendInviteEmail = async (invite) => {
    if (!invite?.code || !invite.email) return;
    try {
      const { data } = await api.post(`/invites/${invite.code}/resend-email`);
      if (data?.email_sent) {
        toast.success(`Email invite resent to ${invite.email}`);
      } else {
        toast.warning(`We couldn't email ${invite.email}`, {
          description: "Copy or share the invite link instead.",
        });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't resend email";
      toast.error(typeof msg === "string" ? msg : "Couldn't resend email");
    }
  };

  const archivePlayer = async (pid) => {
    if (!window.confirm(`Archive this ${terms.athlete}? They won't count toward your active limit.`)) return;
    try {
      await api.post(`/players/${pid}/archive`);
      toast.success(`${terms.Athlete} archived`);
      invalidateNearLimitCache();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not archive");
    }
  };

  const reactivatePlayer = async (pid) => {
    try {
      await api.post(`/players/${pid}/reactivate`);
      toast.success(`${terms.Athlete} reactivated`);
      invalidateNearLimitCache();
      load();
    } catch (err) {
      showPlanLimitToast(err, navigate, { fallback: `Could not reactivate ${terms.athlete}` });
    }
  };

  const removePlayer = async (pid) => {
    if (!window.confirm(`Permanently remove this ${terms.athlete}? This cannot be undone.`)) return;
    try {
      await api.delete(`/players/${pid}`);
      toast.success(`${terms.Athlete} removed`);
      load();
    } catch {
      toast.error("Could not remove");
    }
  };

  const openInviteForPlayer = (pid) => {
    setInvitePlayerId(pid);
    setInviteEmail("");
    setInviteLink("");
    setInviteFromExisting(false);
    setInviteMode("choose");
    setInviteOpen(true);
  };

  // Open the invite dialog directly to the share panel for a player that
  // already has a pending (unaccepted) invite. Skips the form step entirely.
  const openExistingInvite = (pid) => {
    const existing = pendingInviteByPlayer[pid];
    if (!existing) return;
    setInvitePlayerId(pid);
    setInviteEmail(existing.email || "");
    setInviteLink(existing.link || "");
    setInviteFromExisting(true);
    setInviteMode("choose");
    setInviteOpen(true);
  };

  // Pure invite-creation call (no form event) used by both the "Share"
  // and "Copy" fast paths on the method chooser, plus the email-mode
  // submit handler. Reuses an already-pending invite when one exists for
  // the same player to avoid churning tokens when the coach re-opens the
  // dialog.
  //
  // `updateState`: when true (default) mutates the dialog view state —
  // `inviteLink`, `inviteEmail`, `inviteFromExisting` — which causes the
  // dialog to swap from the method chooser to the share panel on next
  // render. The Share/Copy fast paths pass `updateState:false` because
  // they intentionally keep the chooser mounted so that:
  //   • The native share sheet isn't stacked over a Squad Fee modal.
  //   • Dismissing the native sheet returns the coach to the chooser
  //     (rather than the share-panel view, which would surface the raw
  //     URL — cluttered and confusing after a cancel).
  // Returns the invite object (with `.link`) or throws.
  const submitInvite = async ({ email, updateState = true } = {}) => {
    const existing = invitePlayerId ? pendingInviteByPlayer[invitePlayerId] : null;
    // Reuse the existing pending link ONLY when the coach isn't attaching
    // a new email — attaching an email is meant to trigger the outbound
    // send, so we always cut a fresh invite in that case.
    if (existing && !email) {
      if (updateState) {
        setInviteLink(existing.link);
        setInviteEmail(existing.email || "");
        setInviteFromExisting(true);
      }
      return existing;
    }
    const body = { team_id: id, player_id: invitePlayerId || null };
    const emailTrim = (email || "").trim();
    if (emailTrim) body.email = emailTrim;
    const { data } = await api.post("/invites", body);
    if (updateState) {
      setInviteLink(data.link);
      setInviteEmail(data.email || emailTrim || "");
      setInviteFromExisting(false);
    }
    if (invitePlayerId) {
      setPendingInviteByPlayer((p) => ({ ...p, [invitePlayerId]: data }));
    }
    return data;
  };

  // "Share invite link" fast path. Creates (or reuses) the invite, then
  // hands the link to the OS share sheet.
  //
  // Modal state contract (fixes iPhone stacked-modal report):
  //   • We DELIBERATELY do NOT set `inviteLink` before calling
  //     `navigator.share`. Setting it would swap the dialog to the share
  //     panel behind the native sheet — the visual stack the coach saw
  //     on their iPhone. Instead the chooser stays mounted underneath.
  //   • On successful share → close + reset the whole dialog (via
  //     Radix's onOpenChange which wipes state).
  //   • On AbortError (user swipes the sheet away) → leave the chooser
  //     open so they can pick Copy or Email without re-tapping the
  //     player row.
  //   • On desktop (no navigator.share) → advance to the share panel so
  //     the coach can copy or use a mailto/wa.me/sms: deep-link.
  const handleChooseShare = async () => {
    setInviteSubmitting(true);
    try {
      // updateState:false → chooser stays mounted, no visual stack.
      const inv = await submitInvite({ updateState: false });
      const canNative = typeof navigator !== "undefined" && typeof navigator.share === "function";
      if (!canNative) {
        // Desktop fallback: promote dialog to share panel now so the
        // coach has copy + deep-link options in one place.
        setInviteLink(inv.link);
        setInviteEmail(inv.email || "");
        setInviteFromExisting(false);
        return;
      }
      try {
        await navigator.share({
          title: `Join our ${terms.team} on Squad Fee`,
          text: `You've been invited to join ${team?.name || `our ${terms.team}`} on Squad Fee. Tap the link to join:`,
          url: inv.link,
        });
        toast.success("Invite shared");
        // Success path only: dismiss the whole dialog.
        setInviteOpen(false);
      } catch (e) {
        if (e && e.name === "AbortError") {
          // Coach dismissed the native sheet — return them to the
          // chooser so they can pick another method. Dialog was never
          // closed and `invitePlayerId` is preserved, so no state loss.
          return;
        }
        // Non-abort share failure (unsupported target, etc.). Surface
        // a toast and keep the chooser open so the coach can retry
        // with Copy or Email.
        toast.error("Could not share invite");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create invite link");
    } finally {
      setInviteSubmitting(false);
    }
  };

  // "Copy invite link" fast path. Same modal-state contract as Share —
  // keep the chooser mounted so a copy failure doesn't strand the coach.
  // On success we close the dialog so they can paste immediately.
  const handleChooseCopy = async () => {
    setInviteSubmitting(true);
    try {
      const inv = await submitInvite({ updateState: false });
      await copyText(inv.link, "Invite link copied");
      setInviteOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create invite link");
    } finally {
      setInviteSubmitting(false);
    }
  };

  // "Send by email" path — reveals the email input on the same dialog.
  const handleChooseEmail = () => {
    setInviteMode("email");
  };

  // Email-mode submit — creates the invite with the parent's email, which
  // triggers the backend's outbound Resend email. The response carries
  // `email_sent` (true / false / null); we branch the toast so the coach
  // isn't misled when Resend rejected the recipient. Either way the
  // dialog advances to the share panel so the copy/share fallback is
  // always one tap away.
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setInviteSubmitting(true);
    try {
      const created = await submitInvite({ email: inviteEmail });
      if (created?.email_sent) {
        toast.success(`Invite email sent to ${inviteEmail}`);
      } else if (created?.email_sent === false) {
        toast.warning(`We couldn't email ${inviteEmail}`, {
          description: "The invite link is ready — copy or share it instead.",
        });
      }
      // Falls through to the share panel because `inviteLink` is now set.
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send invite");
    } finally {
      setInviteSubmitting(false);
    }
  };

  // Player name derived from `invitePlayerId` for the confirmation strip
  // ("Inviting a parent for Jason Hernandez") so the coach always sees
  // which player they're linking without having to pick again.
  const invitePlayerName = (() => {
    if (!invitePlayerId) return "";
    const p = (players || []).find((x) => x.id === invitePlayerId);
    if (!p) return "";
    return `${p.first_name} ${p.last_name}`.trim();
  })();

  // Single source of truth for the body text used by share-sheet,
  // sms:, whatsapp, and mailto deep-links. Includes the link at the end
  // so even apps that don't preserve the URL field still send something
  // tappable.
  const shareText = () => {
    const teamLabel = team?.name || `our ${terms.team}`;
    return `You've been invited to join ${teamLabel} on Squad Fee. Tap the link to join: ${inviteLink}`;
  };

  // Quick copy helper used by both the dialog and the More-menu shortcut.
  const copyText = async (text, okMsg = "Invite link copied") => {
    try { await navigator.clipboard.writeText(text); toast.success(okMsg); }
    catch { toast.error("Could not copy link"); }
  };

  // Direct native-share for a specific link. Used by the player More-menu
  // "Share Invite Link" shortcut so the coach skips the dialog entirely.
  // Falls back to copy when navigator.share isn't supported (desktop).
  const shareLinkDirect = async (link, email) => {
    const teamLabel = team?.name || `our ${terms.team}`;
    const text = `You've been invited to join ${teamLabel} on Squad Fee. Tap the link to join: ${link}`;
    const shareData = {
      title: `Join our ${terms.team} on Squad Fee`,
      text: `You've been invited to join ${teamLabel} on Squad Fee. Tap the link to join:`,
      url: link,
    };
    if (typeof navigator !== "undefined" && navigator.share) {
      try { await navigator.share(shareData); toast.success("Invite shared"); return; }
      catch (e) { if (e && e.name === "AbortError") return; }
    }
    copyText(text, "Invite link copied");
  };

  // Detect whether native share is available — used to conditionally hide
  // the More-menu "Share Invite Link" item on browsers that don't support
  // navigator.share (e.g. most desktop browsers).
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Invite link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const shareLink = async () => {
    const shareData = {
      title: `Join our ${terms.team} on Squad Fee`,
      text: `You've been invited to join ${team?.name || `our ${terms.team}`} on Squad Fee. Tap the link to join:`,
      url: inviteLink,
    };
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share(shareData);
        toast.success("Invite shared");
        return;
      } catch (e) {
        if (e && e.name === "AbortError") return;
      }
    }
    copyLink();
  };

  if (!team) return <AppLayout><p className="text-ink-500">Loading…</p></AppLayout>;

  // --- Team-management handlers ------------------------------------------
  // All four actions rely on backend ownership guards (`_require_coach_owns_team`
  // returns 404 for cross-tenant attempts), so the client can be optimistic
  // and let the server enforce authority. The API paths are:
  //   • PATCH  /teams/{id}                → edit
  //   • POST   /teams/{id}/archive        → soft-delete (preserves history)
  //   • POST   /teams/{id}/reactivate     → restore (may 402 for plan cap)
  //   • DELETE /teams/{id}/permanent      → hard-delete (409 if not empty)
  const isArchived = team?.status === "archived";

  const openEditDialog = () => {
    setEditForm({
      name: team?.name || "",
      sport: team?.sport || "",
      age_group: team?.age_group || "",
      season: team?.season || "",
    });
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setEditSubmitting(true);
    try {
      await api.patch(`/teams/${id}`, {
        name: editForm.name.trim(),
        sport: editForm.sport.trim(),
        age_group: editForm.age_group.trim(),
        season: editForm.season.trim(),
      });
      toast.success(`${terms.Team} updated`);
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Could not update ${terms.team}`);
    } finally {
      setEditSubmitting(false);
    }
  };

  const archiveThisTeam = async () => {
    if (!window.confirm(`Archive "${team.name}"? It will no longer count toward your active limit. All ${terms.athletes}, fees and payment history stay attached — you can restore anytime.`)) return;
    setTeamActionSubmitting(true);
    try {
      await api.post(`/teams/${id}/archive`);
      toast.success(`${terms.Team} archived`);
      invalidateNearLimitCache();
      // Kick the coach back to the Teams list — an archived team's
      // detail view is still readable (and shows the Reactivate action
      // via `isArchived`), but returning to the list is the more common
      // intent right after archiving.
      navigate("/coach/teams");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Could not archive ${terms.team}`);
    } finally {
      setTeamActionSubmitting(false);
    }
  };

  const reactivateThisTeam = async () => {
    setTeamActionSubmitting(true);
    try {
      await api.post(`/teams/${id}/reactivate`);
      toast.success(`${terms.Team} reactivated`);
      invalidateNearLimitCache();
      load();
    } catch (err) {
      // Reactivation can 402 when the coach is already at their plan's
      // team cap — reuse the shared upgrade CTA toast so the copy stays
      // consistent with team-creation and player-reactivation flows.
      showPlanLimitToast(err, navigate, { fallback: `Could not reactivate ${terms.team}` });
    } finally {
      setTeamActionSubmitting(false);
    }
  };

  const openDeleteDialog = async () => {
    // Preflight the safety check so the dialog can either:
    //   (a) render a clear "Type the name to confirm" form when safe, or
    //   (b) render a friendly "Not safe — has N players, M fees…"
    //       message that redirects the coach to Archive instead.
    setDeleteChecking(true);
    setDeletePreflight(null);
    setDeleteConfirmText("");
    setDeleteOpen(true);
    try {
      // We hit a lightweight probe endpoint via the same DELETE (dry-run
      // by peeking at existing counts client-side). Simplest reliable
      // approach: fetch players + charges + payments totals we already
      // maintain in this page's state.
      const dep = {
        players: (players || []).length,
        charges: (teamCharges || []).length,
        payment_records: Object.values(paymentsByPlayer || {}).reduce((a, arr) => a + (arr || []).length, 0),
      };
      const total = dep.players + dep.charges + dep.payment_records;
      setDeletePreflight({ safe: total === 0, dependencies: dep });
    } finally {
      setDeleteChecking(false);
    }
  };

  const submitPermanentDelete = async () => {
    if (!deletePreflight?.safe) return;
    if (deleteConfirmText.trim() !== (team?.name || "").trim()) {
      toast.error(`Type the ${terms.team} name exactly to confirm.`);
      return;
    }
    setDeleteSubmitting(true);
    try {
      await api.delete(`/teams/${id}/permanent`);
      toast.success(`${terms.Team} deleted`);
      setDeleteOpen(false);
      invalidateNearLimitCache();
      navigate("/coach/teams");
    } catch (err) {
      // Server may reject if a dependency was added between preflight
      // and submit (rare race). Surface the server's message verbatim
      // and reload so the UI reflects reality.
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.kind === "team_has_history") {
        setDeletePreflight({ safe: false, dependencies: detail.dependencies || {} });
        toast.error(detail.message || `Could not delete ${terms.team}`);
      } else {
        toast.error(formatApiErrorDetail(detail) || `Could not delete ${terms.team}`);
        setDeleteOpen(false);
      }
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // For each player, decide the most useful action right now.
  // No parent → Invite Parent ; submitted → Review ; owes → Remind ; else → View
  const actionFor = (p) => {
    if (p.archived) return { kind: "none" };
    if (!p.parents || p.parents.length === 0) return { kind: "invite" };
    const recs = paymentsByPlayer[p.id] || [];
    if (recs.some((r) => r.status === "submitted")) {
      const rec = recs.find((r) => r.status === "submitted");
      return { kind: "review", rec };
    }
    if (recs.some((r) => r.status === "unpaid" || r.status === "rejected")) {
      const rec = recs.find((r) => r.status === "unpaid" || r.status === "rejected");
      return { kind: "remind", rec };
    }
    if (recs.some((r) => r.status === "confirmed" || r.status === "waived")) {
      const rec = recs.find((r) => r.status === "confirmed" || r.status === "waived");
      return { kind: "view", rec };
    }
    return { kind: "view" };
  };

  return (
    <AppLayout>
      <Link to={back.to} className="inline-flex items-center gap-1 text-sm font-semibold text-ink-500 hover:text-ink-900" data-testid="team-detail-back">
        <ArrowLeft className="h-4 w-4" /> Back to {back.label}
      </Link>

      <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-3xl font-bold text-ink-900 sm:text-4xl">{team.name}</h1>
            {isArchived ? (
              <span
                className="inline-flex shrink-0 items-center rounded-full bg-ink-100 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-ink-600"
                data-testid="team-archived-badge"
              >
                Archived
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-base text-ink-600">{team.sport} · {normalizeAgeGroup(team.age_group)}{team.season ? ` · ${team.season}` : ""}</p>
        </div>

        {/* Team management menu — Edit / Archive / Reactivate / Delete.
            Kept as an unobtrusive icon-only button so it doesn't compete
            with the primary Quick Actions row below. */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="h-10 border-ink-300 text-ink-700 hover:bg-ink-100"
              data-testid="team-manage-menu"
              title={`${terms.Team} settings`}
              aria-label={`${terms.Team} settings`}
              disabled={teamActionSubmitting}
            >
              <Settings className="mr-1 h-4 w-4" /> Manage
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56 p-1">
            <button
              type="button"
              onClick={openEditDialog}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-ink-900 hover:bg-ink-100"
              data-testid="team-manage-edit"
            >
              <Pencil className="h-4 w-4" /> Edit {terms.team}
            </button>
            {isArchived ? (
              <button
                type="button"
                onClick={reactivateThisTeam}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-ink-900 hover:bg-ink-100"
                data-testid="team-manage-reactivate"
                disabled={teamActionSubmitting}
              >
                <RotateCcw className="h-4 w-4" /> Reactivate {terms.team}
              </button>
            ) : (
              <button
                type="button"
                onClick={archiveThisTeam}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-ink-900 hover:bg-ink-100"
                data-testid="team-manage-archive"
                disabled={teamActionSubmitting}
              >
                <Archive className="h-4 w-4" /> Archive {terms.team}
              </button>
            )}
            <div className="my-1 h-px bg-ink-100" />
            <button
              type="button"
              onClick={openDeleteDialog}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-rose-600 hover:bg-rose-50"
              data-testid="team-manage-delete"
              disabled={teamActionSubmitting}
            >
              <Trash2 className="h-4 w-4" /> Delete permanently
            </button>
          </PopoverContent>
        </Popover>
      </div>

      {/* Team Snapshot — three plain numbers, just like Home but scoped to
          this team. While the first fetch is in flight `snapshot` is
          `null`; we render a skeleton of pulsing bars instead of
          committing to `$0.00 / 0 / 0` (the P0 flicker the coach saw on
          iPhone). Real values render as soon as `load()` resolves. */}
      <section className="mt-6 rounded-2xl border border-ink-100 bg-white p-5 shadow-sm" data-testid="team-snapshot">
        <p className="text-xs font-bold uppercase tracking-wide text-ink-500">{terms.Team} Snapshot</p>
        {snapshot === null ? (
          <div className="mt-2 divide-y divide-ink-100" data-testid="team-snapshot-loading" aria-busy="true" aria-live="polite">
            <div className="flex items-baseline justify-between gap-4 py-3">
              <span className="text-sm font-medium text-ink-600">Still owed</span>
              <span className="inline-block h-7 w-24 animate-pulse rounded bg-ink-100" />
            </div>
            <div className="flex items-baseline justify-between gap-4 py-3">
              <span className="text-sm font-medium text-ink-600">Unpaid {terms.athletes}</span>
              <span className="inline-block h-7 w-10 animate-pulse rounded bg-ink-100" />
            </div>
            <div className="flex items-baseline justify-between gap-4 py-3">
              <span className="text-sm font-medium text-ink-600">Payments to review</span>
              <span className="inline-block h-7 w-10 animate-pulse rounded bg-ink-100" />
            </div>
          </div>
        ) : (
          <div className="mt-2 divide-y divide-ink-100">
            <div className="flex items-baseline justify-between gap-4 py-3" data-testid="team-snap-still-owed">
              <span className="text-sm font-medium text-ink-600">Still owed</span>
              <span className={`font-heading text-2xl font-bold ${snapshot.owed > 0 ? "text-rose-700" : "text-ink-900"}`}>
                {formatCurrency(snapshot.owed)}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-4 py-3" data-testid="team-snap-unpaid-players">
              <span className="text-sm font-medium text-ink-600">Unpaid {terms.athletes}</span>
              <span className={`font-heading text-2xl font-bold ${snapshot.unpaid_players > 0 ? "text-rose-700" : "text-ink-900"}`}>
                {snapshot.unpaid_players}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-4 py-3" data-testid="team-snap-to-review">
              <span className="text-sm font-medium text-ink-600">Payments to review</span>
              <span className={`font-heading text-2xl font-bold ${snapshot.submitted_count > 0 ? "text-amber-700" : "text-ink-900"}`}>
                {snapshot.submitted_count}
              </span>
            </div>
          </div>
        )}
      </section>

      {/* Quick actions row */}
      <section className="mt-6" data-testid="team-quick-actions">
        <div className="grid gap-3 sm:grid-cols-3">
          <MiniAction
            to={withReturnTo(`/coach/payments?status=submitted&team_id=${id}`, { from: fromHere, label: team?.name })}
            icon={Inbox}
            label="Review Payments"
            accent="amber"
            testid="team-action-review"
          />
          <MiniAction
            to={withReturnTo(`/coach/charges/new`, { from: fromHere, label: team.name })}
            icon={Plus}
            label="Add Fee"
            accent="brand"
            testid="team-action-add-fee"
          />
          <MiniAction
            onClick={() => setPlayerOpen(true)}
            icon={UserPlus}
            label={terms.add_athlete}
            accent="ink"
            testid="team-action-add-player"
          />
        </div>
      </section>

      {/* Add Player dialog (triggered by Quick action above) */}
      <Dialog open={playerOpen} onOpenChange={(o) => { if (!o) closeAddPlayer(); else setPlayerOpen(o); }}>
        <DialogContent>
          {!postAddInvite ? (
            <>
              <DialogHeader><DialogTitle>{terms.add_athlete}</DialogTitle></DialogHeader>
              <form onSubmit={addPlayer} className="space-y-4" data-testid="player-create-form">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>First name</Label><Input required value={playerForm.first_name} onChange={(e) => setPlayerForm((p) => ({ ...p, first_name: e.target.value }))} className="h-12" data-testid="player-first-input" /></div>
                  <div><Label>Last name</Label><Input required value={playerForm.last_name} onChange={(e) => setPlayerForm((p) => ({ ...p, last_name: e.target.value }))} className="h-12" data-testid="player-last-input" /></div>
                </div>
                <div><Label>Jersey (optional)</Label><Input value={playerForm.jersey} onChange={(e) => setPlayerForm((p) => ({ ...p, jersey: e.target.value }))} className="h-12" data-testid="player-jersey-input" /></div>
                <div>
                  <Label>Parent Email — optional</Label>
                  <Input
                    type="email"
                    placeholder="parent@example.com"
                    value={playerForm.parent_email}
                    onChange={(e) => setPlayerForm((p) => ({ ...p, parent_email: e.target.value }))}
                    className="h-12"
                    data-testid="player-parent-email-input"
                  />
                  <p className="mt-1 text-xs text-ink-500">
                    Add an email if you want Squad Fee to send the invite automatically.
                    If not, you can copy or share an invite link after adding the {terms.athlete}.
                  </p>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={addSubmitting} className="h-12 bg-brand font-semibold text-white hover:bg-brand-hover" data-testid="player-create-submit">
                    {addSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (terms.add_athlete)}
                  </Button>
                </DialogFooter>
              </form>
            </>
          ) : (
            /* POST-ADD OPTIONS PANEL — Send email / Copy / Share / Skip */
            <div className="space-y-4" data-testid="post-add-invite-panel">
              <DialogHeader>
                <DialogTitle>Invite parent for {postAddInvite.playerName}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-ink-700">
                {postAddInvite.email
                  ? `Invite is ready for ${postAddInvite.email}. Send it however works best for this parent.`
                  : "Link-only invite is ready. Copy or share it with the parent through any app on your phone."}
              </p>
              <div className="rounded-lg border border-ink-200 bg-ink-100/70 p-3 text-sm">
                <code className="block break-all text-ink-900" data-testid="post-add-invite-link">{postAddInvite.link}</code>
              </div>
              <div className="grid gap-2">
                {postAddInvite.email ? (
                  <Button
                    type="button"
                    onClick={sendEmailInviteForPostAdd}
                    className="h-12 w-full bg-brand font-semibold text-white hover:bg-brand-hover"
                    data-testid="post-add-send-email"
                  >
                    <Mail className="mr-2 h-4 w-4" /> Send email invite
                  </Button>
                ) : null}
                <div className="grid gap-2 sm:grid-cols-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 w-full border-ink-300 font-semibold text-ink-900 hover:bg-ink-100"
                    onClick={() => copyText(postAddInvite.link, "Invite link copied")}
                    data-testid="post-add-copy"
                  >
                    <Copy className="mr-2 h-4 w-4" /> Copy invite link
                  </Button>
                  <Button
                    type="button"
                    className={`h-12 w-full font-semibold ${postAddInvite.email ? "border border-ink-300 bg-white text-ink-900 hover:bg-ink-100" : "bg-brand text-white hover:bg-brand-hover"}`}
                    onClick={() => shareLinkDirect(postAddInvite.link, postAddInvite.email)}
                    data-testid="post-add-share"
                  >
                    <Share2 className="mr-2 h-4 w-4" /> Share invite link
                  </Button>
                </div>
                <button
                  type="button"
                  onClick={closeAddPlayer}
                  className="mt-1 h-10 text-sm font-semibold text-ink-500 hover:text-ink-900"
                  data-testid="post-add-skip"
                >
                  Skip for now
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invite Parent dialog (triggered from a player row) */}
      <Dialog open={inviteOpen} onOpenChange={(o) => { setInviteOpen(o); if (!o) { setInviteLink(""); setInviteEmail(""); setInvitePlayerId(""); setInviteFromExisting(false); setInviteMode("choose"); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {inviteLink
                ? (inviteFromExisting ? "Share invite link" : "Invite ready")
                : inviteMode === "email"
                  ? "Send by email"
                  : "How would you like to invite the parent?"}
            </DialogTitle>
          </DialogHeader>
          {!inviteLink && inviteMode === "choose" ? (
            /* Method chooser — the first thing a coach sees. Email is
               deliberately the LAST option so coaches never feel they
               need a parent's address to invite them. */
            <div className="space-y-3" data-testid="invite-method-chooser">
              {invitePlayerName ? (
                <p className="text-sm text-ink-600" data-testid="invite-player-context">
                  Inviting a parent for <span className="font-semibold text-ink-900">{invitePlayerName}</span>
                </p>
              ) : null}

              {/* 1. Share — recommended */}
              <button
                type="button"
                onClick={handleChooseShare}
                disabled={inviteSubmitting}
                data-testid="invite-choose-share"
                className="flex w-full items-start gap-3 rounded-xl border-2 border-brand bg-brand/5 p-4 text-left transition hover:bg-brand/10 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand text-white">
                  <Share2 className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="font-heading text-base font-bold text-ink-900">Share invite link</span>
                    <span className="inline-flex items-center rounded-full bg-brand px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                      Recommended
                    </span>
                  </span>
                  <span className="mt-1 block text-sm text-ink-600">
                    Send the invitation using text, WhatsApp, email, or another app on your phone.
                  </span>
                </span>
              </button>

              {/* 2. Copy */}
              <button
                type="button"
                onClick={handleChooseCopy}
                disabled={inviteSubmitting}
                data-testid="invite-choose-copy"
                className="flex w-full items-start gap-3 rounded-xl border border-ink-200 bg-white p-4 text-left transition hover:bg-ink-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink-100 text-ink-900">
                  <Copy className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-heading text-base font-bold text-ink-900">Copy invite link</span>
                  <span className="mt-1 block text-sm text-ink-600">
                    Copy the link to send it yourself or post it in a {terms.team} message.
                  </span>
                </span>
              </button>

              {/* 3. Email — last, deliberately understated. */}
              <button
                type="button"
                onClick={handleChooseEmail}
                disabled={inviteSubmitting}
                data-testid="invite-choose-email"
                className="flex w-full items-start gap-3 rounded-xl border border-ink-200 bg-white p-4 text-left transition hover:bg-ink-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink-100 text-ink-900">
                  <Mail className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-heading text-base font-bold text-ink-900">Send by email</span>
                  <span className="mt-1 block text-sm text-ink-600">
                    Squad Fee will email the invitation to the parent for you.
                  </span>
                </span>
              </button>
            </div>
          ) : null}

          {!inviteLink && inviteMode === "email" ? (
            /* Email-only form. Reveals the address input on demand for
               coaches who explicitly want Squad Fee to send the email. */
            <form onSubmit={handleEmailSubmit} className="space-y-4" data-testid="invite-form">
              {invitePlayerName ? (
                <p className="text-sm text-ink-600" data-testid="invite-player-context">
                  Inviting a parent for <span className="font-semibold text-ink-900">{invitePlayerName}</span>
                </p>
              ) : null}
              <div>
                <Label>Parent email</Label>
                <Input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="parent@example.com"
                  className="h-12"
                  data-testid="invite-email-input"
                  autoFocus
                />
                <p className="mt-1 text-xs text-ink-500">
                  We&rsquo;ll email a secure invite link to this address.
                </p>
              </div>
              <DialogFooter className="gap-2 sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 border-ink-300 font-semibold text-ink-900 hover:bg-ink-100"
                  onClick={() => setInviteMode("choose")}
                  disabled={inviteSubmitting}
                  data-testid="invite-email-back"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={inviteSubmitting || !(inviteEmail || "").trim()}
                  className="h-12 bg-brand font-semibold text-white hover:bg-brand-hover"
                  data-testid="invite-submit"
                >
                  {inviteSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send email invite"}
                </Button>
              </DialogFooter>
            </form>
          ) : null}
          {inviteLink ? (
            <div className="space-y-4" data-testid="invite-share-panel">
              <div>
                <p className="font-semibold text-ink-900">{inviteFromExisting ? "Pending invite" : "Invite ready"}</p>
                <p className="text-sm text-ink-500">
                  {inviteFromExisting
                    ? `This parent hasn't accepted yet. Send the link again through any app on your phone.`
                    : (inviteEmail || "").trim()
                      ? `Send this link to the parent through any app on your phone.`
                      : `Link-only invite. Text or share this link directly with the parent.`}
                </p>
                {(inviteEmail || "").trim() ? (
                  <p className="mt-1 text-xs text-ink-500">
                    {inviteFromExisting ? "Sent to " : "Tied to "}
                    <span className="font-semibold text-ink-700">{inviteEmail}</span>
                  </p>
                ) : null}
              </div>

              {/* Read-only link preview */}
              <div className="rounded-lg border border-ink-200 bg-ink-100/70 p-3 text-sm">
                <code className="block break-all text-ink-900" data-testid="invite-link-text">{inviteLink}</code>
              </div>

              {/* Two equal primary actions — both labels explicit so a coach
                  doesn't have to interpret an icon. */}
              <div className="grid gap-2 sm:grid-cols-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 w-full border-ink-300 font-semibold text-ink-900 hover:bg-ink-100"
                  onClick={copyLink}
                  data-testid="invite-copy-button"
                >
                  <Copy className="mr-2 h-4 w-4" /> Copy Invite Link
                </Button>
                <Button
                  type="button"
                  className="h-12 w-full bg-brand font-semibold text-white hover:bg-brand-hover"
                  onClick={shareLink}
                  data-testid="invite-share-button"
                >
                  <Share2 className="mr-2 h-4 w-4" /> Share Invite Link
                </Button>
              </div>

              {/* Convenience deep-links — these open the user's chosen app
                  pre-filled with the invite link. No backend, no contact
                  sync, no sending. Works on both mobile and desktop. */}
              <div className="space-y-2 pt-1">
                <p className="text-xs font-bold uppercase tracking-wide text-ink-500">Send through</p>
                <div className="grid grid-cols-3 gap-2">
                  <a
                    href={`sms:?&body=${encodeURIComponent(shareText())}`}
                    data-testid="invite-send-sms"
                    className="flex flex-col items-center justify-center gap-1 rounded-lg border border-ink-200 bg-white p-3 text-xs font-semibold text-ink-900 hover:bg-ink-100"
                  >
                    <MessageSquare className="h-5 w-5 text-brand" />
                    Text / iMessage
                  </a>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(shareText())}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="invite-send-whatsapp"
                    className="flex flex-col items-center justify-center gap-1 rounded-lg border border-ink-200 bg-white p-3 text-xs font-semibold text-ink-900 hover:bg-ink-100"
                  >
                    <MessageCircle className="h-5 w-5 text-emerald-600" />
                    WhatsApp
                  </a>
                  <a
                    href={`mailto:${encodeURIComponent(inviteEmail || "")}?subject=${encodeURIComponent(`Join our ${terms.team} on Squad Fee`)}&body=${encodeURIComponent(shareText())}`}
                    data-testid="invite-send-email"
                    className="flex flex-col items-center justify-center gap-1 rounded-lg border border-ink-200 bg-white p-3 text-xs font-semibold text-ink-900 hover:bg-ink-100"
                  >
                    <Mail className="h-5 w-5 text-ink-700" />
                    Email
                  </a>
                </div>
                <p className="text-center text-xs text-ink-500">
                  Opens the app on your phone. Squad Fee doesn&apos;t send the message for you yet.
                </p>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Fees — every card is a tappable link to Fee Detail */}
      <section className="mt-8" data-testid="team-fees-section">
        <div className="mb-3 flex items-baseline justify-between gap-3">
          <h2 className="font-heading text-xl font-bold text-ink-900">
            Fees · {teamCharges.length}
          </h2>
          <Link to={withReturnTo("/coach/charges/new", { from: fromHere, label: team.name })} data-testid="team-fees-add">
            <Button className="h-9 bg-brand px-3 text-sm font-semibold text-white hover:bg-brand-hover">
              <Plus className="mr-1 h-4 w-4" /> Add Fee
            </Button>
          </Link>
        </div>
        <div className="overflow-hidden rounded-2xl border border-ink-100 bg-white shadow-sm">
          {teamCharges.length === 0 ? (
            <div className="py-10 text-center" data-testid="team-fees-empty">
              <p className="font-semibold text-ink-900">No fees on this {terms.team} yet.</p>
              <p className="text-sm text-ink-500">Create your first fee to begin collecting payments.</p>
              <Link to={withReturnTo("/coach/charges/new", { from: fromHere, label: team.name })} data-testid="team-fees-empty-cta" className="mt-4 inline-block">
                <Button className="h-11 bg-brand px-5 font-semibold text-white hover:bg-brand-hover">
                  <Plus className="mr-1 h-4 w-4" /> Add Fee
                </Button>
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-ink-100">
              {teamCharges.map((c) => {
                const isCanceled = c.status === "canceled";
                return (
                <li
                  key={c.id}
                  data-testid={`team-fee-row-${c.id}`}
                  className={isCanceled ? "opacity-70 grayscale-[35%]" : ""}
                  data-canceled={isCanceled ? "true" : "false"}
                >
                  <Link
                    to={withReturnTo(`/coach/charges/${c.id}`, { from: fromHere, label: team.name })}
                    className="flex items-center gap-3 px-4 py-4 transition-colors hover:bg-ink-100/40 focus:outline-none focus:bg-ink-100/60 active:bg-ink-100/80"
                    data-testid={`team-fee-link-${c.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <p className="truncate font-semibold text-ink-900">{c.title}</p>
                        <StatusBadge status={c.status} />
                      </div>
                      <p className="mt-1 text-xs text-ink-500">
                        {formatCurrency(c.amount)} · Due {formatDate(c.due_date)}
                        {typeof c.confirmed_count === "number"
                          ? ` · ${c.confirmed_count} paid`
                          : ""}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <DueChip dueDate={c.due_date} chargeStatus={c.status} />
                      <ChevronRight className="h-5 w-5 text-ink-400" aria-hidden="true" />
                    </div>
                  </Link>
                </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {/* Roster — three-state filter [All | Active | Archived] over the
          full player list. Financial totals in the Team Snapshot above
          are UNAFFECTED — this is a pure display filter. */}
      <section className="mt-8" data-testid="team-roster-section">
        {/* Derive counts + visible list from the full `players` array so
            the filter is a client-side pass, not a re-fetch. */}
        <RosterFilterHeader
          filter={rosterFilter}
          setFilter={setRosterFilter}
          players={players}
          terms={terms}
        />
        <div className="mt-3 overflow-hidden rounded-2xl border border-ink-100 bg-white shadow-sm">
          {(() => {
            const visiblePlayers =
              rosterFilter === "active"   ? players.filter((p) => !p.archived) :
              rosterFilter === "archived" ? players.filter((p) =>  p.archived) :
                                            players;
            if (visiblePlayers.length === 0) {
              // Empty-state copy is filter-aware so a coach who lands on
              // "Archived" doesn't get told to "Add your first player."
              const isActiveEmpty = rosterFilter === "active" && players.every((p) => !p.archived);
              return (
                <div className="py-10 text-center" data-testid="roster-empty">
                  <p className="font-semibold text-ink-900">
                    {rosterFilter === "archived"
                      ? `No archived ${terms.athletes}`
                      : rosterFilter === "active" && players.length > 0
                      ? `No active ${terms.athletes}`
                      : `Add your first ${terms.athlete} to start tracking dues.`}
                  </p>
                  <p className="text-sm text-ink-500">
                    {rosterFilter === "archived"
                      ? `Archive an active ${terms.athlete} to free up a plan spot.`
                      : rosterFilter === "active" && players.length > 0
                      ? `Every ${terms.athlete} on this ${terms.team} is currently archived. Switch to All or Archived to view them.`
                      : `${terms.Athletes} are how parents get connected to fees.`}
                  </p>
                  {rosterFilter !== "archived" && players.length === 0 ? (
                    <Button
                      onClick={() => setPlayerOpen(true)}
                      className="mt-4 h-11 bg-brand px-5 font-semibold text-white hover:bg-brand-hover"
                      data-testid="roster-empty-cta"
                    >
                      Add {terms.Athlete}
                    </Button>
                  ) : null}
                </div>
              );
            }
            return (
              <ul className="divide-y divide-ink-100">
                {visiblePlayers.map((p) => {
                const act = actionFor(p);
                return (
                  <li
                    key={p.id}
                    className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-ink-100/40"
                    data-testid={`player-row-${p.id}`}
                  >
                    {/* Player-info block is the ledger link. The quick-action
                        buttons and More menu live as SIBLINGS of this Link so
                        their clicks don't accidentally trigger navigation. */}
                    <Link
                      to={withReturnTo(`/coach/players/${p.id}`, { from: fromHere, label: team.name })}
                      className="min-w-0 flex-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded"
                      data-testid={`player-ledger-link-${p.id}`}
                    >
                      <p className="font-semibold text-ink-900">
                        {p.first_name} {p.last_name} {p.jersey ? <span className="text-ink-500">#{p.jersey}</span> : null}
                        {p.archived ? (
                          <span className="ml-2 inline-block rounded-full bg-ink-100 px-2 py-0.5 text-xs font-medium text-ink-500">
                            Archived
                          </span>
                        ) : null}
                      </p>
                      <p className="truncate text-xs text-ink-500">
                        {p.parents?.length ? p.parents.map((u) => u.email).join(", ") : "No parent linked yet"}
                      </p>
                    </Link>

                    {/* Primary action — picks the most useful next step for this player */}
                    <div className="shrink-0">
                      {act.kind === "invite" ? (
                        <Button
                          variant="outline"
                          className="h-10 border-ink-300 font-semibold text-ink-900 hover:bg-ink-100"
                          onClick={() => openInviteForPlayer(p.id)}
                          data-testid={`player-action-invite-${p.id}`}
                        >
                          <Mail className="mr-1 h-4 w-4" /> Invite Parent
                        </Button>
                      ) : act.kind === "review" ? (
                        <Link to={withReturnTo(`/coach/payments?status=submitted`, { from: fromHere, label: team?.name })} data-testid={`player-action-review-${p.id}`}>
                          <Button className="h-10 bg-amber-500 font-semibold text-white hover:bg-amber-600">
                            <Inbox className="mr-1 h-4 w-4" /> Review
                          </Button>
                        </Link>
                      ) : act.kind === "remind" ? (
                        <Link to={withReturnTo(`/coach/payments?status=unpaid`, { from: fromHere, label: team?.name })} data-testid={`player-action-remind-${p.id}`}>
                          <Button className="h-10 bg-brand font-semibold text-white hover:bg-brand-hover">
                            <Send className="mr-1 h-4 w-4" /> Remind
                          </Button>
                        </Link>
                      ) : act.kind === "view" ? (
                        // Both the player-row tap and this button must land
                        // on the same canonical Player Financial Profile
                        // (PlayerLedger). Previously "View" jumped to the
                        // org-wide `/coach/payments?status=paid` list,
                        // which was a different surface entirely.
                        <Link to={withReturnTo(`/coach/players/${p.id}`, { from: fromHere, label: team?.name })} data-testid={`player-action-view-${p.id}`}>
                          <Button variant="outline" className="h-10 border-ink-300 font-semibold text-ink-900 hover:bg-ink-100">
                            View <ChevronRight className="ml-1 h-4 w-4" />
                          </Button>
                        </Link>
                      ) : null}
                    </div>

                    {/* More menu — archive / reactivate / remove live here, off the main view */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-10 w-10 shrink-0 text-ink-500 hover:bg-ink-100"
                          data-testid={`player-more-${p.id}`}
                          title="More options"
                          aria-label="More options"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-56 p-1">
                        {p.archived ? (
                          <>
                            <button
                              type="button"
                              onClick={() => reactivatePlayer(p.id)}
                              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-ink-900 hover:bg-ink-100"
                              data-testid={`player-reactivate-${p.id}`}
                            >
                              <RotateCcw className="h-4 w-4" /> Reactivate
                            </button>
                            <button
                              type="button"
                              onClick={() => removePlayer(p.id)}
                              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-rose-600 hover:bg-rose-50"
                              data-testid={`player-delete-${p.id}`}
                            >
                              <Trash2 className="h-4 w-4" /> Delete permanently
                            </button>
                          </>
                        ) : (
                          <>
                            {/* Pending-invite shortcuts — only shown when this
                                player has an unaccepted invite already on file. */}
                            {pendingInviteByPlayer[p.id] ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => openExistingInvite(p.id)}
                                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-ink-900 hover:bg-ink-100"
                                  data-testid={`player-invite-show-${p.id}`}
                                >
                                  <Mail className="h-4 w-4" /> Show invite link
                                </button>
                                <button
                                  type="button"
                                  onClick={() => copyText(pendingInviteByPlayer[p.id].link, "Invite link copied")}
                                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-ink-900 hover:bg-ink-100"
                                  data-testid={`player-invite-copy-${p.id}`}
                                >
                                  <Copy className="h-4 w-4" /> Copy invite link
                                </button>
                                {canShare ? (
                                  <button
                                    type="button"
                                    onClick={() => shareLinkDirect(pendingInviteByPlayer[p.id].link, pendingInviteByPlayer[p.id].email)}
                                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-ink-900 hover:bg-ink-100"
                                    data-testid={`player-invite-share-${p.id}`}
                                  >
                                    <Share2 className="h-4 w-4" /> Share invite link
                                  </button>
                                ) : null}
                                {/* Resend email — only when the invite has an email on file. */}
                                {pendingInviteByPlayer[p.id].email ? (
                                  <button
                                    type="button"
                                    onClick={() => resendInviteEmail(pendingInviteByPlayer[p.id])}
                                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-ink-900 hover:bg-ink-100"
                                    data-testid={`player-invite-resend-${p.id}`}
                                  >
                                    <Send className="h-4 w-4" /> Resend email invite
                                  </button>
                                ) : null}
                                {/* visual divider before destructive items */}
                                <div className="my-1 h-px bg-ink-100" />
                              </>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => archivePlayer(p.id)}
                              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-ink-900 hover:bg-ink-100"
                              data-testid={`player-archive-${p.id}`}
                            >
                              <Archive className="h-4 w-4" /> Archive player
                            </button>
                          </>
                        )}
                      </PopoverContent>
                    </Popover>
                  </li>
                );
              })}
            </ul>
            );
          })()}
        </div>
      </section>
      {/* Edit Team dialog — updates name / sport / age / season in-place.
          Preserves the team id so every downstream relationship
          (players, invites, fees, payment_records, receipts) stays
          attached without duplication. */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit {terms.team}</DialogTitle></DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4" data-testid="team-edit-form">
            <div>
              <Label>{terms.Team} name</Label>
              <Input
                required
                value={editForm.name}
                onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                className="h-12"
                data-testid="team-edit-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Sport</Label>
                <Input
                  required
                  value={editForm.sport}
                  onChange={(e) => setEditForm((p) => ({ ...p, sport: e.target.value }))}
                  className="h-12"
                  data-testid="team-edit-sport-input"
                />
              </div>
              <div>
                <Label>Age group</Label>
                <Input
                  required
                  value={editForm.age_group}
                  onChange={(e) => setEditForm((p) => ({ ...p, age_group: e.target.value }))}
                  className="h-12"
                  data-testid="team-edit-age-input"
                />
              </div>
            </div>
            <div>
              <Label>Season</Label>
              <Input
                required
                value={editForm.season}
                onChange={(e) => setEditForm((p) => ({ ...p, season: e.target.value }))}
                className="h-12"
                data-testid="team-edit-season-input"
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={editSubmitting}
                className="h-12 bg-brand font-semibold text-white hover:bg-brand-hover"
                data-testid="team-edit-submit"
              >
                {editSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Permanent-delete dialog. Two branches:
          – Safe: type-to-confirm gate + red confirm button.
          – Not safe: friendly explainer + "Archive instead" primary CTA. */}
      <Dialog open={deleteOpen} onOpenChange={(o) => { setDeleteOpen(o); if (!o) { setDeleteConfirmText(""); setDeletePreflight(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {deletePreflight?.safe ? `Delete "${team.name}"?` : `Can't permanently delete "${team.name}"`}
            </DialogTitle>
          </DialogHeader>

          {deleteChecking || !deletePreflight ? (
            <div className="flex items-center gap-2 py-6 text-ink-500" data-testid="team-delete-checking">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking safety…
            </div>
          ) : deletePreflight.safe ? (
            <div className="space-y-4" data-testid="team-delete-safe">
              <p className="text-sm text-ink-700">
                This permanently removes this {terms.team} and cannot be undone. There are no
                {" "}
                {terms.athletes}, fees or payments on this {terms.team}, so nothing else is affected.
              </p>
              <div>
                <Label>Type the {terms.team} name to confirm</Label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={team.name}
                  className="h-12"
                  data-testid="team-delete-confirm-input"
                  autoFocus
                />
              </div>
              <DialogFooter className="gap-2 sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 border-ink-300 font-semibold text-ink-900 hover:bg-ink-100"
                  onClick={() => setDeleteOpen(false)}
                  data-testid="team-delete-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="h-12 bg-rose-600 font-semibold text-white hover:bg-rose-700"
                  disabled={deleteSubmitting || deleteConfirmText.trim() !== (team?.name || "").trim()}
                  onClick={submitPermanentDelete}
                  data-testid="team-delete-confirm-submit"
                >
                  {deleteSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete permanently"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4" data-testid="team-delete-unsafe">
              <p className="text-sm text-ink-700">
                This {terms.team} has records that shouldn&rsquo;t be silently destroyed. Archive
                keeps every record and can be restored anytime.
              </p>
              <ul className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="team-delete-deps">
                {(deletePreflight.dependencies?.players || 0) > 0 ? <li>{deletePreflight.dependencies.players} {terms.athletes}</li> : null}
                {(deletePreflight.dependencies?.charges || 0) > 0 ? <li>{deletePreflight.dependencies.charges} fee(s)</li> : null}
                {(deletePreflight.dependencies?.payment_records || 0) > 0 ? <li>{deletePreflight.dependencies.payment_records} payment record(s)</li> : null}
                {(deletePreflight.dependencies?.invites || 0) > 0 ? <li>{deletePreflight.dependencies.invites} invite(s)</li> : null}
              </ul>
              <DialogFooter className="gap-2 sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 border-ink-300 font-semibold text-ink-900 hover:bg-ink-100"
                  onClick={() => setDeleteOpen(false)}
                  data-testid="team-delete-unsafe-cancel"
                >
                  Keep {terms.team}
                </Button>
                <Button
                  type="button"
                  className="h-12 bg-brand font-semibold text-white hover:bg-brand-hover"
                  onClick={() => { setDeleteOpen(false); archiveThisTeam(); }}
                  data-testid="team-delete-archive-instead"
                >
                  <Archive className="mr-2 h-4 w-4" /> Archive instead
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
